<template>
  <div class="service-overview-container relative min-h-screen py-20">
    <!-- Background Elements -->
    <div class="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 opacity-40"></div>
    <div class="absolute inset-0 bg-pattern opacity-5"></div>
    
    <!-- Header Section -->
    <div class="relative z-10 text-center mb-16">
      <h2 class="text-4xl md:text-6xl font-bold text-white mb-6">
        Our Service <span class="text-primary">Capabilities</span>
      </h2>
      <p class="text-xl text-white/80 max-w-3xl mx-auto">
        With 13 service bays, STE is dedicated to keep your costs down and your stainless steel equipment operating at optimum levels.
      </p>
    </div>

    <!-- Three Main Service Areas -->
    <div class="relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-8xl mx-auto px-6">
      <!-- Expanded Service Department -->
      <div class="service-card group" ref="card1">
        <div class="card-image">
          <img 
            src="/img/service/expanded-service.png" 
            alt="Expanded Service Department - 13 Service Bays"
            class="w-full h-64 object-cover"
            loading="lazy"
          />
          <div class="image-overlay">
            <div class="overlay-icon">
              <font-awesome-icon :icon="['fas', 'tools']" />
            </div>
          </div>
        </div>
        <div class="card-content">
          <h3 class="card-title">Expanded Service Department</h3>
          <p class="card-description">
            With 13 service bays, we have the capacity to work on your trailer from start to finish and get it back to you so you can put it back on the road and making money.
          </p>
          <div class="learn-more-btn">
            <font-awesome-icon :icon="['fas', 'arrow-right']" />
            <span>Learn more</span>
          </div>
        </div>
      </div>
      <!-- We work on All SS Trailer Makes -->
      <div class="service-card group" ref="card2">
        <div class="card-image">
          <img 
            src="/img/service/all-trailer-makes.png" 
            alt="We work on All SS Trailer Makes"
            class="w-full h-64 object-cover"
            loading="lazy"
          />
          <div class="image-overlay">
            <div class="overlay-icon">
              <font-awesome-icon :icon="['fas', 'truck']" />
            </div>
          </div>
        </div>
        <div class="card-content">
          <h3 class="card-title">We work on All Stainless Steel Trailer Brands</h3>
          <p class="card-description">
            We will service any brand of stainless steel tank trailer, just not every type. We're confident in our abilities to repair most, so give us a call to discuss your needs.
          </p>
          <div class="learn-more-btn">
            <font-awesome-icon :icon="['fas', 'arrow-right']" />
            <span>Learn more</span>
          </div>
        </div>
      </div>
      <!-- No Project is Too Big, or Small -->
      <div class="service-card group" ref="card3">
        <div class="card-image">
          <img 
            src="/img/service/no-project-too-big.png" 
            alt="No Project is Too Big, or Small"
            class="w-full h-64 object-cover"
            loading="lazy"
          />
          <div class="image-overlay">
            <div class="overlay-icon">
              <font-awesome-icon :icon="['fas', 'cogs']" />
            </div>
          </div>
        </div>
        <div class="card-content">
          <h3 class="card-title">No Project is Too Big, or Small</h3>
          <p class="card-description">
            Whether you need a complete suspension replacement or just a repair on axle or hub components, we have the expertise and equipment to get you back on the road swiftly.
          </p>
          <div class="learn-more-btn">
            <font-awesome-icon :icon="['fas', 'arrow-right']" />
            <span>Learn more</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  name: 'ServiceOverviewSection',
  mounted() {
    this.setupIntersectionObserver();
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    setupIntersectionObserver() {
      const cards = [this.$refs.card1, this.$refs.card2, this.$refs.card3];
      
      cards.forEach((card, index) => {
        if (card) {
          const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                setTimeout(() => {
                  entry.target.classList.add('animate-in');
                }, index * 200);
              }
            });
          }, { threshold: 0.1 });
          
          observer.observe(card);
        }
      });
    }
  }
};
</script>
<style scoped>
.bg-pattern {
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.service-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1);
  opacity: 0;
  transform: translateY(50px);
  backdrop-filter: blur(10px);
}

.service-card.animate-in {
  opacity: 1;
  transform: translateY(0);
}

.service-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  border-color: rgba(162, 42, 33, 0.3);
}

.card-image {
  position: relative;
  overflow: hidden;
}

.card-image img {
  transition: transform 0.5s ease;
  filter: brightness(0.8);
}

.service-card:hover .card-image img {
  transform: scale(1.1);
  filter: brightness(1);
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(162, 42, 33, 0.8), rgba(0, 0, 0, 0.6));
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.service-card:hover .image-overlay {
  opacity: 1;
}

.overlay-icon {
  color: white;
  font-size: 3rem;
  transform: scale(0.8);
  transition: transform 0.3s ease;
}

.service-card:hover .overlay-icon {
  transform: scale(1);
}

.card-content {
  padding: 2rem;
  color: white;
}

.card-title {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
  color: white;
}

.card-description {
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  margin-bottom: 1.5rem;
}

.learn-more-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #a22a21;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.learn-more-btn:hover {
  color: #c23529;
  transform: translateX(4px);
}

@media (max-width: 1024px) {
  .service-card {
    margin-bottom: 2rem;
  }
}
</style>